"use client";

import authApi from "@/lib/auth-api.client";
import { resetMixpanel } from "@/lib/services";
import {
  clearClarityMixpanelProps,
  setAnalyticsUser,
} from "@/lib/services/ClarityMixpanelBridge";

import { supabase } from "@/lib/supabase";
import {
  WalkthroughFlags,
  resolveWalkthroughFlags,
} from "@/lib/walkthrough-defaults";
import {
  AuthChangeEvent,
  AuthError,
  Session,
  User,
} from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  AuthResult,
  CustomAuthResponse,
  SignUpPayload,
  SupabaseAuthContextType,
} from "./auth-provider/types";
import { runPostLoginNavigation } from "./auth-provider/navigation";
import {
  trackEmailSignIn,
  trackEmailSignUp,
  trackOAuthAuthEvent,
} from "./auth-provider/tracking";
import { deriveUserProperties } from "./auth-provider/derive-user";
import { useMeFallback } from "./auth-provider/use-me-fallback";
import { useImpersonation } from "./auth-provider/use-impersonation";

export {
  getAccessToken,
  getCurrentSession,
  getCurrentUser,
  isUserAuthenticated,
  clearImpersonation,
  isImpersonating,
} from "./auth-provider/external";

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(
  undefined,
);

export const useAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a SupabaseAuthProvider");
  }
  return context;
};

export function SupabaseAuthProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const lastMetadataLogRef = useRef<string>("");
  // Reentrancy guard for handlePostLoginNavigation. Kept in a ref (not on
  // window) so it does not leak across provider instances and is guaranteed
  // to reset via the try/finally around the navigation body.
  const navigationInProgressRef = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  const { impersonatedUserId, impersonatedMetadata, clearImpersonation } =
    useImpersonation();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handlePostLoginNavigation = useCallback(
    (session: Session, options?: { ignorePublicRouteGuard?: boolean }) =>
      runPostLoginNavigation(session, navigationInProgressRef, options),
    [],
  );

  useEffect(() => {
    if (!mounted) return;

    let subscription: { unsubscribe: () => void } | null = null;

    // Get initial session
    const initializeAuth = async () => {
      try {
        // First, set up the auth state listener
        const { data } = supabase.auth.onAuthStateChange(
          async (event: AuthChangeEvent, session: Session | null) => {
            setSession(session);
            setUser(session?.user ?? null);
            setAnalyticsUser(
              session?.user
                ? {
                    id: session.user.id,
                    email: session.user.email ?? null,
                    user_type: session.user.user_metadata?.user_type ?? null,
                  }
                : null,
            );
            setLoading(false);

            // Track OAuth sign-in/sign-up (Google, LinkedIn). No-op for email.
            if (session && event === "SIGNED_IN") {
              trackOAuthAuthEvent(session);
            }

            // Handle navigation for any auth event that provides a session
            // This covers SIGNED_IN, TOKEN_REFRESHED, and other session-related events
            // Skip navigation if already on a dashboard or auth pages (prevents unwanted redirects after signup)
            const currentPath =
              typeof window !== "undefined" ? window.location.pathname : "";
            const isOnDashboard =
              currentPath.startsWith("/company-dashboard") ||
              currentPath.startsWith("/jobber-dashboard");
            const isOnAuthPage = currentPath.includes("/auth/");

            // Navigate only on a REAL login (SIGNED_IN), and never from a
            // dashboard or auth page (signIn()/signUp() own their post-login
            // nav, and we avoid double redirects after signup).
            //
            // We deliberately do NOT navigate on INITIAL_SESSION /
            // TOKEN_REFRESHED: those fire on every page load while a session
            // exists and bounced authenticated users off public routes. The
            // "authenticated user stuck on an auth page" case is handled in
            // the auth forms themselves, which can tell a fresh login apart
            // from a stale client-only session.
            if (
              session &&
              event === "SIGNED_IN" &&
              !isOnDashboard &&
              !isOnAuthPage
            ) {
              await handlePostLoginNavigation(session);
            }
          },
        );

        subscription = data.subscription;

        // Then get the initial session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        setSession(session);
        setUser(session?.user ?? null);
        setAnalyticsUser(
          session?.user
            ? {
                id: session.user.id,
                email: session.user.email ?? null,
                user_type: session.user.user_metadata?.user_type ?? null,
              }
            : null,
        );
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      subscription?.unsubscribe();
    };
  }, [mounted, handlePostLoginNavigation]);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: Record<string, unknown>,
      shouldRedirect: boolean = true,
    ): Promise<AuthResult> => {
      try {
        // Prepare payload for custom auth API
        const phoneValue = metadata?.phone as
          | {
              country_id?: string;
              country_phone_code?: string;
              phone_number?: string;
            }
          | undefined;

        // Support both direct metadata properties and nested phone object
        const countryId =
          (metadata?.country_id as string) || phoneValue?.country_id;
        const phoneNumber =
          (metadata?.phone_number as string) || phoneValue?.phone_number;
        const countryPhoneCode =
          (metadata?.country_phone_code as string) ||
          phoneValue?.country_phone_code;

        // Only include extra fields if they have valid values
        const extra: Record<string, any> = {};
        if (countryId) extra.country_id = countryId;
        if (phoneNumber) extra.phone_number = phoneNumber;
        if (countryPhoneCode) extra.country_phone_code = countryPhoneCode;

        const payload: SignUpPayload = {
          firstName: (metadata?.first_name as string) || "-",
          lastName: (metadata?.last_name as string) || "-",
          email,
          password,
          confirmPassword: password, // Assuming confirmation happens client-side before this
          ...(Object.keys(extra).length > 0 ? { extra } : {}),
        };

        // Call custom auth API
        const response = await authApi.post<CustomAuthResponse>(
          "/auth/signup",
          payload,
        );
        const authData = response.data;

        // Create a Supabase-compatible session object from custom response
        const customSession: Partial<Session> = {
          access_token: authData.accessToken,
          refresh_token: authData.refreshToken,
          expires_at: authData.expiresToken,
          expires_in: authData.expiresToken - Math.floor(Date.now() / 1000),
          token_type: "bearer",
          user: {
            id: authData.id,
            email: authData.email,
            user_metadata: authData.userMetadata,
            aud: "authenticated",
            created_at: authData.createdAt,
          } as User,
        };

        // Set session in Supabase client (for middleware and SSR)
        await supabase.auth.setSession({
          access_token: authData.accessToken,
          refresh_token: authData.refreshToken,
        });

        // Update provider state immediately (don't wait for onAuthStateChange)
        setSession(customSession as Session);
        setUser(customSession.user as User);
        setAnalyticsUser({
          id: authData.id,
          email: authData.email,
          user_type: authData.userMetadata?.user_type ?? null,
        });
        setLoading(false);

        // Track signup event in Mixpanel (safe: wraps its own try/catch so
        // tracking failures never break the signup flow).
        trackEmailSignUp({
          authData,
          firstName: payload.firstName,
          lastName: payload.lastName,
          countryId: extra.country_id,
          phoneNumber: extra.phone_number,
        });

        // After sign up, redirect to user type selection
        // Only redirect if shouldRedirect is true (default behavior) and we are not in a flow that must stay put
        if (typeof window !== "undefined") {
          const stayOnCurrentRoute =
            window.location.pathname.startsWith("/company-dashboard") ||
            window.location.pathname.startsWith("/jobber-dashboard");
          if (shouldRedirect && !stayOnCurrentRoute) {
            window.location.href = "/onboarding/user-type";
          }
        }

        return {
          data: {
            user: customSession.user,
            session: customSession,
          },
          error: null,
        };
      } catch (error: any) {
        // Solo loggear errores inesperados (no 400/401/409 que son errores de validación)
        const statusCode = error.response?.status;
        if (
          statusCode &&
          statusCode !== 400 &&
          statusCode !== 401 &&
          statusCode !== 409
        ) {
          // Unexpected error - should be logged server-side
        }

        const errorMessage =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          "Sign up failed";
        const authError = new Error(errorMessage) as AuthError;
        authError.name = "AuthError";
        authError.status =
          error.response?.data?.statusCode || error.response?.status;
        return { data: null, error: authError };
      }
    },
    [],
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      try {
        // Call custom auth API
        const response = await authApi.post<CustomAuthResponse>(
          "/auth/signin",
          {
            email,
            password,
          },
        );
        const authData = response.data;

        // Create a Supabase-compatible session object from custom response
        const customSession: Partial<Session> = {
          access_token: authData.accessToken,
          refresh_token: authData.refreshToken,
          expires_at: authData.expiresToken,
          expires_in: authData.expiresToken - Math.floor(Date.now() / 1000),
          token_type: "bearer",
          user: {
            id: authData.id,
            email: authData.email,
            user_metadata: authData.userMetadata,
            aud: "authenticated",
            created_at: authData.createdAt,
          } as User,
        };

        // Set session in Supabase client (for middleware and SSR)
        await supabase.auth.setSession({
          access_token: authData.accessToken,
          refresh_token: authData.refreshToken,
        });

        // Update provider state immediately (don't wait for onAuthStateChange)
        setSession(customSession as Session);
        setUser(customSession.user as User);
        setAnalyticsUser({
          id: authData.id,
          email: authData.email,
          user_type: authData.userMetadata?.user_type ?? null,
        });
        setLoading(false);

        // Track sign-in event in Mixpanel/Clarity.
        trackEmailSignIn(authData);

        // Post-login navigation. The onAuthStateChange listener above skips
        // navigation while the user is on an auth page, so signIn owns the
        // redirect from /auth/sign-in explicitly. The ref-based mutex inside
        // handlePostLoginNavigation prevents any double-fire.
        await handlePostLoginNavigation(customSession as Session);

        return {
          data: {
            user: customSession.user,
            session: customSession,
          },
          error: null,
        };
      } catch (error: any) {
        // Solo loggear errores inesperados (no 400/401 que son credenciales incorrectas)
        const statusCode = error.response?.status;
        if (statusCode && statusCode !== 400 && statusCode !== 401) {
          // Unexpected error - should be logged server-side
        }

        const errorMessage =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          "Sign in failed";
        const authError = new Error(errorMessage) as AuthError;
        authError.name = "AuthError";
        authError.status =
          error.response?.data?.statusCode || error.response?.status;
        return { data: null, error: authError };
      }
    },
    [handlePostLoginNavigation],
  );

  const signInWithProvider = useCallback(
    async (
      provider: "google" | "linkedin_oidc",
      nextPath?: string,
    ): Promise<AuthResult> => {
      // Use NEXT_PUBLIC_SITE_URL for production, fallback to window.location.origin for local dev
      const baseUrl =
        process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;

      // The return path travels in a short-lived cookie rather than as a query
      // param on redirectTo. Supabase matches redirect URLs against an EXACT
      // allow-list, so appending `?next=…` would require every environment's
      // auth config to be updated (and would silently fail where it wasn't).
      // The cookie keeps redirectTo byte-identical to what is already
      // allow-listed. SameSite=Lax survives the provider's top-level GET
      // redirect back to us.
      if (nextPath && typeof document !== "undefined") {
        document.cookie = `jobbi-oauth-next=${encodeURIComponent(nextPath)}; path=/; max-age=600; samesite=lax`;
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${baseUrl}/auth/callback`,
        },
      });
      return { data, error };
    },
    [],
  );

  /**
   * Adopt an already-issued token pair as the active session — same effect as
   * signIn(), but for flows where the backend already ran the auth (it returns
   * the identical `CustomAuthResponse` shape). Used by the public job-apply
   * flow: `/auth/signup-with-application` creates the account AND returns
   * accessToken/refreshToken, so instead of dropping the applicant on
   * /auth/sign-in we log them in here and route them to their dashboard.
   *
   * `redirect` runs post-login navigation with the public-route guard lifted
   * (the caller is still sitting on `/job/...` when this fires).
   */
  const adoptSession = useCallback(
    async (
      authData: CustomAuthResponse,
      { redirect = true }: { redirect?: boolean } = {},
    ): Promise<AuthResult> => {
      try {
        const customSession: Partial<Session> = {
          access_token: authData.accessToken,
          refresh_token: authData.refreshToken,
          expires_at: authData.expiresToken,
          expires_in: authData.expiresToken - Math.floor(Date.now() / 1000),
          token_type: "bearer",
          user: {
            id: authData.id,
            email: authData.email,
            user_metadata: authData.userMetadata,
            aud: "authenticated",
            created_at: authData.createdAt,
          } as User,
        };

        await supabase.auth.setSession({
          access_token: authData.accessToken,
          refresh_token: authData.refreshToken,
        });

        setSession(customSession as Session);
        setUser(customSession.user as User);
        setAnalyticsUser({
          id: authData.id,
          email: authData.email,
          user_type: authData.userMetadata?.user_type ?? null,
        });
        setLoading(false);

        if (redirect) {
          await handlePostLoginNavigation(customSession as Session, {
            ignorePublicRouteGuard: true,
          });
        }

        return {
          data: { user: customSession.user, session: customSession },
          error: null,
        };
      } catch (error: any) {
        const authError = new Error(
          error?.message || "Failed to adopt session",
        ) as AuthError;
        authError.name = "AuthError";
        return { data: null, error: authError };
      }
    },
    [handlePostLoginNavigation],
  );

  const signOut = useCallback(async () => {
    try {
      // Call backend to invalidate session
      await authApi.post("/auth/signout");

      // Clear local Supabase session
      await supabase.auth.signOut();

      // Clear provider state
      setSession(null);
      setUser(null);
      setAnalyticsUser(null);

      // Clear sessionStorage
      if (typeof window !== "undefined") {
        sessionStorage.clear();
        // Clear impersonation data
        localStorage.removeItem("impersonate_user_id");
        localStorage.removeItem("impersonate_metadata");
      }

      // Clear cookies (except userPreference)
      if (typeof document !== "undefined") {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name =
            eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

          // Skip userPreference cookie to preserve user preferences
          if (name.toLowerCase() === "userpreference") {
            continue;
          }

          // Clear cookie by setting it to expire in the past
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
      }

      // Clear Clarity session correlation so the next anonymous session starts clean
      clearClarityMixpanelProps();

      // Redirect to signin page
      window.location.href = "/auth/sign-in";
    } catch (error) {
      // Force clear state even if signOut fails
      setSession(null);
      setUser(null);
      setAnalyticsUser(null);

      // Still clear storage and cookies on error
      if (typeof window !== "undefined") {
        sessionStorage.clear();
        // Clear impersonation data
        localStorage.removeItem("impersonate_user_id");
        localStorage.removeItem("impersonate_metadata");
      }
      if (typeof document !== "undefined") {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name =
            eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

          // Skip userPreference cookie to preserve user preferences
          if (name.toLowerCase() === "userpreference") {
            continue;
          }

          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
      }

      // Clear Clarity props from Mixpanel localStorage on error path too
      clearClarityMixpanelProps();

      // Still redirect to signin on error
      window.location.href = "/auth/sign-in";
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
        setAnalyticsUser({
          id: data.session.user.id,
          email: data.session.user.email ?? null,
          user_type: data.session.user.user_metadata?.user_type ?? null,
        });
      }
    } catch (error) {
      resetMixpanel();
      // If refresh fails, sign out
      await signOut();
    }
  }, [signOut]);

  // Fallback /api/me: cuando la sesión está activa pero el user_metadata no
  // trae user_account_id, full_name o phone válidos, los resolvemos consultando
  // /me. Sin esto, useDashboardWorker se queda en enabled:false y la Profile
  // Card del jobber no carga tras login.
  const { meFallback, refreshMeFallback } = useMeFallback({
    session,
    user,
    impersonatedUserId,
    impersonatedMetadata,
  });

  // All derived scalars/objects live inside a single useMemo so their
  // references stay stable across renders when the underlying inputs
  // (user, impersonation, meFallback) do not change.
  const derived = useMemo(
    () =>
      deriveUserProperties({
        user,
        impersonatedUserId,
        impersonatedMetadata,
        meFallback,
      }),
    [user, impersonatedUserId, impersonatedMetadata, meFallback],
  );

  const {
    metadata,
    userId,
    userEmail,
    userType,
    userAccountId,
    sanitizedMetadataFullName,
    fullName,
    pictureUrl,
    emailVerified,
    phoneVerified,
    completed,
    stepForm,
    companyId,
    commercialName,
    legalName,
    companyPictureUrl,
    infoId,
    profileHealth,
    urlCv,
    sanitizedMetadataPhoneNumber,
    phoneNumber,
    phoneCountryId,
  } = derived;

  useEffect(() => {
    const metadataSignature = JSON.stringify(metadata ?? {});
    const signature = `${userId ?? "null"}:${metadataSignature}`;

    if (lastMetadataLogRef.current === signature) {
      return;
    }

    lastMetadataLogRef.current = signature;
  }, [userId, userType, phoneNumber, phoneCountryId, metadata]);

  // Walkthrough flags
  const walkthrough: WalkthroughFlags | null = useMemo(
    () =>
      metadata.walkthrough
        ? resolveWalkthroughFlags(
            metadata.walkthrough as Record<string, unknown>,
          )
        : null,
    [metadata],
  );

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      isAuthenticated: !!session && !!user,
      accessToken: session?.access_token ?? null,
      refreshToken: session?.refresh_token ?? null,
      userType,
      userId,
      userEmail,
      userAccountId,
      fullName,
      pictureUrl,
      emailVerified,
      phoneVerified,
      completed,
      stepForm,
      companyId,
      commercialName,
      legalName,
      companyPictureUrl,
      infoId,
      profileHealth,
      urlCv,
      phoneNumber,
      phoneCountryId,
      walkthrough,
      signUp,
      signIn,
      signInWithProvider,
      adoptSession,
      signOut,
      refreshSession,
      refreshMeFallback,
      isImpersonating: !!impersonatedUserId,
      clearImpersonation,
    }),
    [
      user,
      session,
      loading,
      userType,
      userId,
      userEmail,
      userAccountId,
      fullName,
      pictureUrl,
      emailVerified,
      phoneVerified,
      completed,
      stepForm,
      companyId,
      commercialName,
      legalName,
      companyPictureUrl,
      infoId,
      profileHealth,
      urlCv,
      phoneNumber,
      phoneCountryId,
      walkthrough,
      signUp,
      signIn,
      signInWithProvider,
      adoptSession,
      signOut,
      refreshSession,
      refreshMeFallback,
      impersonatedUserId,
      clearImpersonation,
    ],
  );

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}
